import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ManualEmailTestingPanel = ({ onTestEmailSent }) => {
  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientName: '',
    emailType: 'activation',
    customSubject: '',
    customMessage: ''
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const emailTemplates = [
    { value: 'activation', label: 'Account Activation Email', function: 'send-account-activation-email' },
    { value: 'billing', label: 'Billing Notification', function: 'send-billing-notification' },
    { value: 'compliance', label: 'Compliance Alert', function: 'send-compliance-email' },
    { value: 'payment_reminder', label: 'Payment Reminder', function: 'send-payment-reminder' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendTestEmail = async () => {
    try {
      setSending(true);
      setResult(null);

      if (!formData?.recipientEmail || !formData?.recipientName) {
        setResult({ success: false, message: 'Please fill in all required fields' });
        return;
      }

      const template = emailTemplates?.find(t => t?.value === formData?.emailType);
      
      // Prepare payload based on email type
      let payload = {
        email: formData?.recipientEmail,
        fullName: formData?.recipientName
      };

      if (formData?.emailType === 'activation') {
        payload = {
          ...payload,
          activationToken: `test_${Date.now()}`,
          activationUrl: `${window.location?.origin}/activate?token=test_${Date.now()}`,
          trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()
        };
      }

      const { data, error } = await supabase?.functions?.invoke(template?.function, {
        body: payload
      });

      if (error) throw error;

      setResult({
        success: true,
        message: 'Test email sent successfully!',
        emailId: data?.emailId || data?.messageId,
        timestamp: new Date()?.toISOString(),
        apiResponse: data
      });

      onTestEmailSent?.();
    } catch (error) {
      console.error('Error sending test email:', error);
      setResult({
        success: false,
        message: error?.message || 'Failed to send test email',
        error: error
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="Send" size={24} className="text-blue-600 dark:text-blue-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Manual Email Testing Wizard</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Send test emails with customizable templates and track delivery</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Recipient Email *
          </label>
          <input
            type="email"
            value={formData?.recipientEmail}
            onChange={(e) => handleInputChange('recipientEmail', e?.target?.value)}
            placeholder="test@example.com"
            className="w-full px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Recipient Name *
          </label>
          <input
            type="text"
            value={formData?.recipientName}
            onChange={(e) => handleInputChange('recipientName', e?.target?.value)}
            placeholder="John Doe"
            className="w-full px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Template
          </label>
          <select
            value={formData?.emailType}
            onChange={(e) => handleInputChange('emailType', e?.target?.value)}
            className="w-full px-4 py-2 border border-neutral-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white"
          >
            {emailTemplates?.map((template) => (
              <option key={template?.value} value={template?.value}>
                {template?.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={handleSendTestEmail}
          disabled={sending}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Icon name="Send" size={18} />
              Send Test Email
            </>
          )}
        </button>
      </div>

      {result && (
        <div className={`p-4 rounded-lg border ${
          result?.success 
            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' :'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-3">
            <Icon 
              name={result?.success ? 'CheckCircle' : 'XCircle'} 
              size={20} 
              className={result?.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} 
            />
            <div className="flex-1">
              <p className={`font-medium ${
                result?.success ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'
              }`}>
                {result?.message}
              </p>
              {result?.success && result?.emailId && (
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Email ID: {result?.emailId}
                </p>
              )}
              {result?.success && result?.timestamp && (
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Sent at: {new Date(result?.timestamp)?.toLocaleString()}
                </p>
              )}
              {result?.apiResponse && (
                <details className="mt-2">
                  <summary className="text-sm cursor-pointer text-green-700 dark:text-green-300 hover:underline">
                    View API Response
                  </summary>
                  <pre className="mt-2 p-2 bg-white dark:bg-slate-800 rounded text-xs overflow-x-auto">
                    {JSON.stringify(result?.apiResponse, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualEmailTestingPanel;